"use client";

import { useEffect, useState } from "react";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";
import { sessionStore } from "./session-store";

export interface AuthSessionState {
  user: User | null;
  session: Session | null;
  authEvent: AuthChangeEvent | null;
  isLoading: boolean;
}

export function useAuthSession(): AuthSessionState {
  const [supabase] = useState(() => createClient());
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authEvent, setAuthEvent] = useState<AuthChangeEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const applySession = (nextSession: Session | null, event?: AuthChangeEvent): void => {
      if (!isActive) {
        return;
      }
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      sessionStore.setSession(nextSession);
      if (event) {
        setAuthEvent(event);
      }
    };

    const fetchSession = async (): Promise<void> => {
      const { data } = await supabase.auth.getSession();
      applySession(data.session ?? null);
      setIsLoading(false);
    };

    void fetchSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      applySession(nextSession ?? null, event);
    });

    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, [supabase]);

  return {
    user,
    session,
    authEvent,
    isLoading,
  };
}
